import { useState, useEffect, useRef } from 'react';
import {
  Lock, Save, RefreshCw, Play, CheckCircle, AlertCircle, Clock, History, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog } from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/auth';
import { userApi, syncApi } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { SyncLog } from '@/types';
import type { FlightListItem, DailyEventItem } from '@/types/api.types';
import { format, parseISO } from 'date-fns';
import { parseApiError } from '@/services/api';

export function EflightSyncSection() {
  const { user, updateUser } = useAuthStore();
  const { toast } = useToast();

  // Credentials
  const [eflightData, setEflightData] = useState({
    eflightUsername: user?.eflightUsername || '',
    eflightPassword: '',
  });
  const [isSavingEflight, setIsSavingEflight] = useState(false);

  // Sync settings
  const [syncEnabled, setSyncEnabled] = useState(user?.syncEnabled ?? true);
  const [syncMonths, setSyncMonths] = useState(user?.syncMonths ?? 2);
  const [syncHistoryMonths, setSyncHistoryMonths] = useState(user?.syncHistoryMonths ?? 0);

  // Manual sync
  const [isSyncing, setIsSyncing] = useState(false);

  // Sync logs
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const isFetchingLogs = useRef(false);

  // Batch details dialog
  const [selectedLog, setSelectedLog] = useState<SyncLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [batchDetails, setBatchDetails] = useState<{
    flights: FlightListItem[];
    dailyEvents: DailyEventItem[];
  }>({ flights: [], dailyEvents: [] });

  useEffect(() => {
    fetchLogs(1, false);
  }, []);

  const fetchLogs = async (pageNum = 1, append = false) => {
    if (isFetchingLogs.current) return;
    isFetchingLogs.current = true;
    if (!append) setLogsLoading(true);
    try {
      const response = await syncApi.getLogs({ page: pageNum, pageSize: 3 });
      const data = response.data;
      const results = data.results || [];
      const count = data.count || 0;

      if (append) {
        setLogs((prev) => [...prev, ...results]);
      } else {
        setLogs(results);
      }
      const currentLength = append ? logs.length + results.length : results.length;
      setHasMore(results.length === 3 && currentLength < count);
      setPage(pageNum);
    } catch {
      toast({ title: '获取同步记录失败', variant: 'destructive' });
    } finally {
      setLogsLoading(false);
      isFetchingLogs.current = false;
    }
  };

  const saveEflightCredentials = async () => {
    setIsSavingEflight(true);
    try {
      await userApi.updateEflightCredentials({
        eflightUsername: eflightData.eflightUsername,
        eflightPassword: eflightData.eflightPassword,
      });
      updateUser({ eflightUsername: eflightData.eflightUsername });
      toast({ title: '保存成功', description: 'eFlight 凭证已更新', variant: 'success' });
      setEflightData((prev) => ({ ...prev, eflightPassword: '' }));
    } catch (err: any) {
      const { message } = parseApiError(err);
      toast({ title: '保存失败', description: message, variant: 'destructive' });
    } finally {
      setIsSavingEflight(false);
    }
  };

  const handleSyncToggle = async (enabled: boolean) => {
    setSyncEnabled(enabled);
    try {
      await userApi.updateSyncSettings({ syncEnabled: enabled });
      updateUser({ syncEnabled: enabled });
      toast({ title: '设置已更新', variant: 'success' });
    } catch {
      setSyncEnabled(!enabled);
      toast({ title: '更新失败', variant: 'destructive' });
    }
  };

  const handleMonthsChange = async (months: number) => {
    setSyncMonths(months);
    try {
      await userApi.updateSyncSettings({ syncMonths: months });
      updateUser({ syncMonths: months });
      toast({ title: '设置已更新', variant: 'success' });
    } catch {
      setSyncMonths(user?.syncMonths ?? 2);
      toast({ title: '更新失败', variant: 'destructive' });
    }
  };

  const handleHistoryMonthsChange = async (months: number) => {
    setSyncHistoryMonths(months);
    try {
      await userApi.updateSyncSettings({ syncHistoryMonths: months });
      updateUser({ syncHistoryMonths: months });
      toast({ title: '设置已更新', variant: 'success' });
    } catch {
      setSyncHistoryMonths(user?.syncHistoryMonths ?? 0);
      toast({ title: '更新失败', variant: 'destructive' });
    }
  };

  const handleManualSync = async () => {
    setIsSyncing(true);
    try {
      await syncApi.trigger(syncMonths, syncHistoryMonths);
      toast({ title: '同步已启动', description: '正在同步您的航班数据...', variant: 'success' });
      setTimeout(() => fetchLogs(1, false), 3000);
    } catch (error: any) {
      const { message } = parseApiError(error);
      toast({
        title: '同步失败',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success': return '成功';
      case 'failed': return '失败';
      case 'partial': return '部分成功';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openDetails = async (log: SyncLog) => {
    setSelectedLog(log);
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const response = await syncApi.getLogBatchDetails(log.id);
      const data = response.data.data;
      setBatchDetails({
        flights: data?.flights || [],
        dailyEvents: data?.dailyEvents || [],
      });
    } catch {
      toast({ title: '获取同步详情失败', variant: 'destructive' });
      setBatchDetails({ flights: [], dailyEvents: [] });
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedLog(null);
    setBatchDetails({ flights: [], dailyEvents: [] });
  };

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) return '-';
    try {
      return format(parseISO(value), 'yyyy-MM-dd HH:mm');
    } catch {
      return value;
    }
  };

  const formatDate = (value: string | null | undefined) => {
    if (!value) return '-';
    try {
      return format(parseISO(value), 'yyyy-MM-dd');
    } catch {
      return value;
    }
  };

  return (
    <div className="space-y-6">
      {/* Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            eFlight 账号
          </CardTitle>
          <CardDescription>配置您的 eFlight 系统登录凭证，用于同步航班数据</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eflightUsername">eFlight 用户名</Label>
              <Input
                id="eflightUsername"
                value={eflightData.eflightUsername}
                onChange={(e) => setEflightData((prev) => ({ ...prev, eflightUsername: e.target.value }))}
                placeholder="请输入 eFlight 用户名"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eflightPassword">eFlight 密码</Label>
              <Input
                id="eflightPassword"
                type="password"
                value={eflightData.eflightPassword}
                onChange={(e) => setEflightData((prev) => ({ ...prev, eflightPassword: e.target.value }))}
                placeholder={user?.eflightUsername ? '已设置，输入新密码可修改' : '请输入 eFlight 密码'}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveEflightCredentials} disabled={isSavingEflight}>
              {isSavingEflight ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              保存凭证
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            同步设置
          </CardTitle>
          <CardDescription>配置自动同步行为</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">启用自动同步</p>
              <p className="text-sm text-gray-500">每天凌晨自动同步航班数据</p>
            </div>
            <Switch checked={syncEnabled} onCheckedChange={handleSyncToggle} />
          </div>

          <div>
            <p className="font-medium mb-3">同步未来月份数</p>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 4, 5, 6].map((months) => (
                <button
                  key={months}
                  onClick={() => handleMonthsChange(months)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    syncMonths === months
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {months} 个月
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              自动同步及手动同步默认同步未来 {syncMonths} 个月的航班数据
            </p>
          </div>

          <div>
            <p className="font-medium mb-3">同步历史月份数</p>
            <div className="flex gap-2 flex-wrap">
              {[0, 1, 2, 3, 4, 5, 6].map((months) => (
                <button
                  key={months}
                  onClick={() => handleHistoryMonthsChange(months)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    syncHistoryMonths === months
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {months === 0 ? '不同步' : `${months} 个月`}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              手动同步时额外同步历史 {syncHistoryMonths} 个月的航班数据
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Manual Sync */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="w-5 h-5" />
            手动同步
          </CardTitle>
          <CardDescription>立即触发一次 eFlight 数据同步</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleManualSync} disabled={isSyncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? '同步中...' : '立即同步'}
          </Button>
        </CardContent>
      </Card>

      {/* Sync Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            同步记录
          </CardTitle>
          <CardDescription>历史同步任务日志</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {logsLoading && logs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">暂无同步记录</p>
            </div>
          ) : (
            <div className="divide-y">
              {logs.map((log) => {
                const status = log.status ?? 'unknown';
                return (
                  <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        {getStatusIcon(status)}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium">{log.syncTypeDisplay}同步</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                              {getStatusText(status)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 space-y-0.5">
                            <p>
                              时间范围: {formatDate(log.syncStartDate)} ~ {formatDate(log.syncEndDate)}
                            </p>
                            <p>
                              航班 {log.flightsTotal ?? 0} 个
                              {(log.flightsCreated ?? 0) > 0 && ` · 新增 ${log.flightsCreated}`}
                              {(log.flightsUpdated ?? 0) > 0 && ` · 更新 ${log.flightsUpdated}`}
                              {' · '}
                              日常 {log.dailyTotal ?? 0} 个
                            </p>
                            {log.errorMessage && (
                              <p className="text-red-500">{log.errorMessage}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm text-gray-500 flex-shrink-0">
                        <p>{log.startedAt && format(parseISO(log.startedAt), 'yyyy-MM-dd HH:mm')}</p>
                        {log.durationMs != null && (
                          <p className="mt-1">耗时: {(log.durationMs / 1000).toFixed(1)}s</p>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 h-auto py-1 px-2 text-blue-600 hover:text-blue-700"
                          onClick={() => openDetails(log)}
                        >
                          查看详情
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && (
            <div className="p-4 text-center border-t">
              <Button
                variant="outline"
                onClick={() => fetchLogs(page + 1, true)}
                disabled={logsLoading}
              >
                {logsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                加载更多
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch Details Dialog */}
      <Dialog isOpen={detailsOpen} onClose={closeDetails} title="同步详情">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {selectedLog && (
            <div className="text-sm text-gray-500 space-y-1">
              <p>
                {selectedLog.syncTypeDisplay}同步 · {getStatusText(selectedLog.status)}
              </p>
              <p>
                时间范围: {formatDate(selectedLog.syncStartDate)} ~ {formatDate(selectedLog.syncEndDate)}
              </p>
              <p>
                开始时间: {formatDateTime(selectedLog.startedAt)}
              </p>
              {selectedLog.durationMs != null && (
                <p>耗时: {(selectedLog.durationMs / 1000).toFixed(1)}s</p>
              )}
            </div>
          )}

          {detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              <div>
                <h4 className="font-medium mb-2">
                  航班 ({batchDetails.flights.length})
                </h4>
                {batchDetails.flights.length === 0 ? (
                  <p className="text-sm text-gray-500">本次同步未涉及航班</p>
                ) : (
                  <div className="space-y-2">
                    {batchDetails.flights.map((flight) => (
                      <div
                        key={flight.id}
                        className="bg-gray-50 rounded-lg p-3 text-sm"
                      >
                        <div className="font-medium text-gray-900">
                          {flight.formattedRoute || flight.flightNumbers?.join(', ') || '未知航线'}
                        </div>
                        <div className="text-gray-500 mt-1">
                          {flight.flightNumbers && flight.flightNumbers.length > 0 && (
                            <span className="mr-3">航班号: {flight.flightNumbers.join(', ')}</span>
                          )}
                        </div>
                        <div className="text-gray-500">
                          {formatDateTime(flight.startTime)} ~ {formatDateTime(flight.endTime)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  日常事项 ({batchDetails.dailyEvents.length})
                </h4>
                {batchDetails.dailyEvents.length === 0 ? (
                  <p className="text-sm text-gray-500">本次同步未涉及日常事项</p>
                ) : (
                  <div className="space-y-2">
                    {batchDetails.dailyEvents.map((event) => (
                      <div
                        key={event.id}
                        className="bg-gray-50 rounded-lg p-3 text-sm"
                      >
                        <div className="font-medium text-gray-900">
                          {event.title}
                          <span className="ml-2 text-xs text-gray-500">
                            ({event.eventTypeDisplay})
                          </span>
                        </div>
                        <div className="text-gray-500 mt-1">
                          {formatDateTime(event.startTime)} ~ {formatDateTime(event.endTime)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
}
